import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Map, { Source, Layer, useMap, MapProvider, Popup, NavigationControl, GeoJSONSource } from 'react-map-gl'
import Tile from './Tile'
import { HAS_POINT_COUNT, MAPBOX_TOKEN, MAP_BOUNDS, MARKER_LAYER_LAYOUT, MAX_SPIDERFIER_ZOOM, clusterCountLayerStyle, clusterLayerStyle, shadowLayerStyle, unclusteredActiveLayerStyle, unclusteredLayerStyle } from './constants'
import Skeleton from 'react-loading-skeleton'
import Spiderfy from '@nazka/map-gl-js-spiderfy';
import MapImage from './MapImage'

export interface IMobilePopoutProps {
	data?: any
	bounds?: any
	numberOfFilters: number
	show?: boolean
	isLoggedIn: boolean
	handleStyleLoad: (map: any) => void
	openFilter: () => void
	close: () => void
}

interface IPopupInfo {
	lat: number
	lng: number
	data: any
}

const MobilePopout = ({ numberOfFilters, bounds, data, isLoggedIn, openFilter, close, show }: IMobilePopoutProps ) => {
	const [popupInfo, setPopupInfo] = useState<IPopupInfo|false>(null);
    const [mapObj, setMapObj] = useState<any|null>(null);
    const [showReset, setShowReset] = useState<boolean>(false);
    const spiderfyRef = useRef(null);

	const handleClose = () => {
		setPopupInfo(false)
	}

	const resetBounds = (items: any) => {

		setShowReset(false);

		mapObj.fitBounds(items, {
			padding: 100
		});

	}

    const handleStyleLoad = (_map: any) => {
        setMapObj(_map?.target);
    };

	const increaseZoom = () => {
		mapObj.zoomIn();
		setShowReset(true);
	}

	const decreaseZoom = () => {
		mapObj.zoomOut();
		setShowReset(true);
	}

    useEffect(() => {
        if(bounds && mapObj) {

			resetBounds(bounds)

			// requestAnimationFrame(() => {
			// 	const getBoundsFromViewport = mapObj.getBounds();
			// 	mapObj.setMaxBounds(getBoundsFromViewport);
			// })
        }
    }, [bounds, mapObj])

	const handleZoomEnd = (_map) => {
		// console.log(_map)
        if (!spiderfyRef.current) {
            spiderfyRef.current = new Spiderfy(_map.target, {
                onLeafClick: (feature) => {
					setPopupInfo(() => ({lat: feature.geometry?.coordinates[1], lng: feature.geometry.coordinates[0], data: {
						...feature.properties,
						heroImageUrls: JSON.stringify(feature.properties.heroImageUrls)
					}}));
                },
                onLeafHover: (feature, ev) => {
                    ev.target.getCanvas().style.cursor = feature ? 'pointer' : '';
                },
                minZoomLevel: MAX_SPIDERFIER_ZOOM,
                zoomIncrement: 2,
                closeOnLeafClick: false,
                circleSpiralSwitchover: 500, // match with the old spiderfier implementation
                circleOptions: {
                    leavesSeparation: 100,
                    leavesOffset: [0, 0],
                },
                spiralOptions: {
                    legLengthStart: 100,
                    legLengthFactor: 10,
                    leavesSeparation: 150,
                    leavesOffset: [0, 0],
                },
                spiderLegsAreHidden: false,
                spiderLegsWidth: 2,
                spiderLegsColor: '#FA6469',
                spiderLeavesLayout: MARKER_LAYER_LAYOUT,
                spiderLeavesPaint: {
                    'icon-color': '#FA6469',
                },
                maxLeaves: 255,
                renderMethod: 'flat',
            }); // create a new spiderfy object
            spiderfyRef.current.applyTo('markers-layer-spiderfier'); // apply to a markers layer spiderfier
        }
    };

    const handleMarkersClick = (_ev: any) => {

		_ev.originalEvent.stopPropagation();
		if (!_ev.features?.[0]) {
			return
		}
		switch (_ev.features[0]?.layer?.id) {
			case "unclustered-point":
				// mapObj.setCenter([_ev.features[0].geometry.coordinates[0], _ev.features[0].geometry?.coordinates[1]])
				setPopupInfo({ lat: _ev.features[0].geometry?.coordinates[1], lng: _ev.features[0].geometry.coordinates[0], data: _ev.features[0].properties });
				break;
			case "clusters":
				console.log(mapObj.current)
				const mapboxSource = mapObj?.getSource('my-data') as GeoJSONSource;

				// mapObj.setCenter([_ev.features[0].geometry.coordinates[0], _ev.features[0].geometry?.coordinates[1]])
				mapboxSource.getClusterExpansionZoom(_ev.features[0].id, (err: any, zoom: any) => {
					if (err) { return; }

					mapObj.easeTo({
						center: _ev.lngLat,
						zoom,
						duration: 500
					});
				})
				break;
			default:
				break;
		}
    };

	const isActiveFeature = useMemo(() => ['in', 'id', popupInfo ? popupInfo?.data?.id : ''], [popupInfo]);

	return (
		<div className={["listing-popout-map", show ? 'listing-popout-map-active' : ''].join(' ')}>
			<div className="nav-back">
				<button onClick={close} className='btn close-btn'>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path fillRule="evenodd" clipRule="evenodd" d="M6.26885 7.99999L11.3669 2.79999L9.88236 1.28578L3.2998 7.99999L9.88236 14.7142L11.3669 13.2L6.26885 7.99999Z" fill="#86C0C7"/>
					</svg>
					<span>Back</span>
				</button>
				<button onClick={openFilter} className='btn filter-btn button-secondary'>
					{numberOfFilters > 0 ?
						<span className='filter-number'>{numberOfFilters || 0}</span>
					:
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path fillRule="evenodd" clipRule="evenodd" d="M7.91912 2.58579C8.29419 2.21071 8.8029 2 9.33333 2C9.86377 2 10.3725 2.21071 10.7475 2.58579C10.9605 2.79872 11.1204 3.05473 11.219 3.33333H13.3333C13.7015 3.33333 14 3.63181 14 4C14 4.36819 13.7015 4.66667 13.3333 4.66667H11.219C11.1204 4.94527 10.9605 5.20128 10.7475 5.41421C10.3725 5.78929 9.86377 6 9.33333 6C8.8029 6 8.29419 5.78929 7.91912 5.41421C7.70618 5.20128 7.54622 4.94527 7.44771 4.66667H2.66667C2.29848 4.66667 2 4.36819 2 4C2 3.63181 2.29848 3.33333 2.66667 3.33333H7.44771C7.54622 3.05473 7.70618 2.79872 7.91912 2.58579ZM8.86193 4.4714C8.73694 4.34641 8.66671 4.1769 8.66667 4.00015V4C8.66667 3.82319 8.7369 3.65362 8.86193 3.5286C8.98695 3.40357 9.15652 3.33333 9.33333 3.33333C9.51014 3.33333 9.67971 3.40357 9.80474 3.5286C9.92976 3.65362 10 3.82319 10 4C10 4.17681 9.92976 4.34638 9.80474 4.4714C9.67971 4.59643 9.51014 4.66667 9.33333 4.66667C9.15652 4.66667 8.98695 4.59643 8.86193 4.4714ZM3.91912 6.58579C4.29419 6.21071 4.8029 6 5.33333 6C5.86377 6 6.37247 6.21071 6.74755 6.58579C6.96048 6.79872 7.12045 7.05473 7.21895 7.33333H13.3333C13.7015 7.33333 14 7.63181 14 8C14 8.36819 13.7015 8.66667 13.3333 8.66667H7.21895C7.12045 8.94527 6.96048 9.20128 6.74755 9.41421C6.37247 9.78929 5.86377 10 5.33333 10C4.8029 10 4.29419 9.78929 3.91912 9.41421C3.70618 9.20128 3.54622 8.94527 3.44771 8.66667H2.66667C2.29848 8.66667 2 8.36819 2 8C2 7.63181 2.29848 7.33333 2.66667 7.33333H3.44771C3.54622 7.05473 3.70618 6.79872 3.91912 6.58579ZM5.80474 7.5286C5.92976 7.65362 6 7.82319 6 8C6 8.17681 5.92976 8.34638 5.80474 8.4714C5.67971 8.59643 5.51014 8.66667 5.33333 8.66667C5.15652 8.66667 4.98695 8.59643 4.86193 8.4714C4.7369 8.34638 4.66667 8.17681 4.66667 8C4.66667 7.82319 4.7369 7.65362 4.86193 7.5286C4.98695 7.40357 5.15652 7.33333 5.33333 7.33333C5.51014 7.33333 5.67971 7.40357 5.80474 7.5286ZM11.3333 10C10.8029 10 10.2942 10.2107 9.91912 10.5858C9.70618 10.7987 9.54622 11.0547 9.44771 11.3333H2.66667C2.29848 11.3333 2 11.6318 2 12C2 12.3682 2.29848 12.6667 2.66667 12.6667H9.44771C9.54622 12.9453 9.70618 13.2013 9.91912 13.4142C10.2942 13.7893 10.8029 14 11.3333 14C11.8638 14 12.3725 13.7893 12.7475 13.4142C12.9605 13.2013 13.1204 12.9453 13.219 12.6667H13.3333C13.7015 12.6667 14 12.3682 14 12C14 11.6318 13.7015 11.3333 13.3333 11.3333H13.219C13.1204 11.0547 12.9605 10.7987 12.7475 10.5858C12.3725 10.2107 11.8638 10 11.3333 10ZM12 12C12 11.8232 11.9298 11.6536 11.8047 11.5286C11.6797 11.4036 11.5101 11.3333 11.3333 11.3333C11.1565 11.3333 10.987 11.4036 10.8619 11.5286C10.737 11.6535 10.6668 11.8228 10.6667 11.9994L10.6667 12C10.6667 12.1768 10.7369 12.3464 10.8619 12.4714C10.987 12.5964 11.1565 12.6667 11.3333 12.6667C11.5101 12.6667 11.6797 12.5964 11.8047 12.4714C11.9298 12.3464 12 12.1768 12 12Z" fill="#214359"/>
						</svg>
					}
					<span>Filters</span>
				</button>
			</div>
			<div style={{height: 'calc(100% - 67.5px)', position: 'relative'}}>
				{data?.features ?
					<div className="listing-total">
						{data?.features?.length} Listings
					</div>
				:
					<Skeleton width={82} height={30} containerClassName='listing-total-skeleton' />
				}
				<MapProvider>
					<Map
						maxBounds={MAP_BOUNDS}
						id="mobilePropertyMap"
						initialViewState={{
							latitude: -33.764866900510626,
							longitude: 151.2708529819774,
							zoom: 11,
						}}
						style={{ width: '100%', height: '100%' }}
						mapStyle="mapbox://styles/mapbox/streets-v9"
						interactiveLayerIds={['markers-layer-spiderfier', 'unclustered-point', 'clusters']}
						mapboxAccessToken={MAPBOX_TOKEN}
						onStyleData={handleStyleLoad}
						onClick={handleMarkersClick}
						onZoomEnd={handleZoomEnd}
						onDragEnd={() => {setShowReset(true)}}
						maxZoom={18}
					>
						<MapImage />
						<NavigationControl position="bottom-right" />
						<Source
							id="my-data"
							type="geojson"
							cluster={true}
							clusterMaxZoom={MAX_SPIDERFIER_ZOOM}
							clusterRadius={25}
							data={data}
						>
							{popupInfo && (
								<Popup onClose={handleClose} closeButton={false} key={popupInfo?.data?.id} className='location-modal' style={{maxWidth: 250}} latitude={popupInfo?.lat || 0} longitude={popupInfo?.lng || 0}>
									<Tile data={popupInfo.data} isLoggedIn={isLoggedIn} altStyle={true} />
									<div className='popout-close' onClick={() => {setPopupInfo(null)}}>
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path fillRule="evenodd" clipRule="evenodd" d="M3.52861 3.52858C3.78896 3.26823 4.21107 3.26823 4.47141 3.52858L8.00001 7.05717L11.5286 3.52858C11.789 3.26823 12.2111 3.26823 12.4714 3.52858C12.7318 3.78893 12.7318 4.21104 12.4714 4.47138L8.94282 7.99998L12.4714 11.5286C12.7318 11.7889 12.7318 12.211 12.4714 12.4714C12.2111 12.7317 11.789 12.7317 11.5286 12.4714L8.00001 8.94279L4.47141 12.4714C4.21107 12.7317 3.78896 12.7317 3.52861 12.4714C3.26826 12.211 3.26826 11.7889 3.52861 11.5286L7.0572 7.99998L3.52861 4.47138C3.26826 4.21103 3.26826 3.78892 3.52861 3.52858Z" fill="white"/>
										</svg>
									</div>
								</Popup>
							)}
							<Layer {...shadowLayerStyle} />
							<Layer
								{...unclusteredLayerStyle}
							/>
							<Layer
								id="markers-layer-spiderfier"
								type="symbol"
								filter={HAS_POINT_COUNT}
								layout={MARKER_LAYER_LAYOUT}
								// onMouseEnter={handleMouseEnter}
								// onMouseLeave={handleMouseLeave}
								minzoom={MAX_SPIDERFIER_ZOOM}
							/>
							{/*
							// @ts-ignore */}
							<Layer {...unclusteredActiveLayerStyle} filter={isActiveFeature} />
							<Layer
								{...clusterLayerStyle}
								maxzoom={MAX_SPIDERFIER_ZOOM}
							/>
							<Layer {...clusterCountLayerStyle} />
						</Source>
					</Map>
				</MapProvider>
			</div>
		</div>
	)
}

export default MobilePopout
